import { useMutation, useQuery } from "@tanstack/react-query";
import {
  deleteCoachingClip,
  getCoachingClips,
  type ApiError,
} from "@/lib/api";
import { invalidateScope, useScopedKey } from "@/lib/queryClient";
import { useScopeStore } from "@/stores/scope";

/**
 * Coach > Clips list-page data hooks (M6a).
 *
 * `useCoachClipsList` is the team's coaching clips for the active
 * scope. `useCoachClipMutations` covers the one list-page write
 * path — delete. (Create + edit go through Coach Review's
 * `useSaveCoachingClip` — the Clips list routes INTO Coach Review
 * for those, same pattern as the Notes list.)
 *
 * Scope-keyed + gated on `teamId`, like the other M6a list
 * queries; the delete `invalidateScope`s so the list refetches.
 */

export function useCoachClipsList() {
  const teamId = useScopeStore((s) => s.teamId);
  return useQuery({
    queryKey: useScopedKey("coaching-clips"),
    // Wrap so react-query's QueryFunctionContext isn't forwarded as
    // the optional `matchId` arg — the list page wants ALL clips.
    queryFn: () => getCoachingClips(),
    enabled: !!teamId,
  });
}

export type CoachClipMutations = {
  deleteClip: (id: number) => Promise<{ ok: true }>;
  isPending: boolean;
  error: ApiError | null;
};

export function useCoachClipMutations(): CoachClipMutations {
  const remove = useMutation({
    mutationFn: deleteCoachingClip,
    onSuccess: () => invalidateScope(),
  });
  return {
    deleteClip: (id) => remove.mutateAsync(id),
    isPending: remove.isPending,
    error: (remove.error as ApiError | null) ?? null,
  };
}
